import React, { useState, useMemo, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Typography from '@mui/material/Typography';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import {
  useAccount, 
  useDisconnect, 
  useSwitchChain,
  useBalance,
  useWriteContract,
  useWaitForTransactionReceipt,
  useSendTransaction,
} from 'wagmi';
import { parseUnits } from 'viem';
import { getTokenInfo, getChainId, BLOCK_EXPLORERS } from '../utils/networkTokens';
import debugLog from '../utils/debugLog';
import normalizeWalletError from '../utils/normalizeWalletError';
import { toEpochMs } from '../utils/time';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import Modal from '@mui/material/Modal';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

// ERC20/BEP20 ABI for token transfers
const tokenAbi = [
  {
    name: 'transfer',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' }
    ],
    outputs: [{ name: '', type: 'bool' }]
  }
];

// Define the ButtonDivider component before using it
const ButtonDivider = styled(Divider)(({ theme }) => ({
  height: 24,
  margin: '0 8px',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
}));

const WalletButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#384df5',
  color: 'white',
  '&:hover': {
    backgroundColor: '#2b3eda',
  },
  marginTop: theme.spacing(2),
  width: '100%'
}));

// Replace the formatTokenAmount function (around line 51-72)
const formatTokenAmount = (amount, decimals = 18) => {
  // Handle null, undefined or NaN
  if (amount === null || amount === undefined || (typeof amount === 'number' && isNaN(amount))) {
    return '0';
  }
  
  // Convert to string if it's not already
  const strAmount = String(amount);
  
  // Check if it's in scientific notation (contains 'e' or 'E')
  if (strAmount.includes('e') || strAmount.includes('E')) {
    // Convert scientific notation to a regular decimal string
    const floatVal = parseFloat(strAmount);
    
    // Use BigNumber or similar library for precision if available
    // For now, use a technique that preserves all decimal places
    const str = floatVal.toFixed(decimals + 10); // Add extra precision
    
    // Remove trailing zeros but ensure proper decimal places for parseUnits
    const parts = str.split('.');
    if (parts.length === 2) {
      // Ensure we have at least 'decimals' number of places after decimal
      const decimalPart = parts[1].padEnd(decimals, '0');
      return `${parts[0]}.${decimalPart}`;
    }
    return str;
  }
  
  // For regular number strings, ensure proper decimal places
  if (strAmount.includes('.')) {
    const parts = strAmount.split('.');
    // Ensure we have at least 'decimals' number of places after decimal
    const decimalPart = parts[1].padEnd(decimals, '0');
    return `${parts[0]}.${decimalPart}`;
  }
  
  // For integers, add decimal places
  return `${strAmount}.${'0'.repeat(decimals)}`;
};

// Update the NetworkSwitchModal component
const NetworkSwitchModal = ({ open, onClose, targetNetwork, targetChainId, currentChainId, switchChain }) => {
  const displayCurrentChainId = currentChainId || 'Unknown';
  
  // Function to attempt network switch again
  const handleSwitchNetwork = async () => {
    try {
      const chainIdNumber = typeof targetChainId === 'string' 
        ? parseInt(targetChainId, 10) 
        : targetChainId;
      
      if (switchChain) {
        await switchChain({ chainId: chainIdNumber });
        // Close modal immediately - wagmi will handle the rest
        onClose();
      }
    } catch (error) {
      console.error("Network switch error:", error);
      // Still close the modal so user isn't stuck
      onClose();
      alert("Please switch networks manually in your wallet");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="network-switch-modal-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 360,
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)',
        p: 4,
        outline: 'none',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography id="network-switch-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Network Switch Required
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ 
            bgcolor: 'rgba(0,0,0,0.04)', 
            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' } 
          }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 3,
          p: 2, 
          bgcolor: 'rgba(244, 67, 54, 0.08)', 
          borderRadius: 2,
        }}>
          <Box sx={{ 
            bgcolor: '#f44336', 
            borderRadius: '50%', 
            p: 1, 
            mr: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(244, 67, 54, 0.25)' 
          }}>
            <SwapHorizIcon sx={{ color: 'white' }} />
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Your wallet is on the wrong network
          </Typography>
        </Box>
        
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
          This transaction requires <strong>{targetNetwork}</strong> (Chain ID: {targetChainId}),
          but your wallet is currently connected to Chain ID: {displayCurrentChainId}.
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 3, color: 'text.primary' }}>
          Please open your wallet and switch to <strong>{targetNetwork}</strong>.
        </Typography>
        
        {/* Network selector chip-style button */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            startIcon={<AccountTreeIcon />}
            onClick={handleSwitchNetwork}
            sx={{ 
              borderRadius: 5,
              px: 3,
              py: 1,
              backgroundColor: targetNetwork.toLowerCase().includes('testnet') ? 'rgba(245, 124, 0, 0.08)' : 'rgba(240, 185, 11, 0.08)',
              borderColor: targetNetwork.toLowerCase().includes('testnet') ? '#F57C00' : '#F0B90B',
              color: targetNetwork.toLowerCase().includes('testnet') ? '#F57C00' : '#F0B90B',
              fontWeight: 600,
              '&:hover': { 
                backgroundColor: targetNetwork.toLowerCase().includes('testnet') ? 'rgba(245, 124, 0, 0.12)' : 'rgba(240, 185, 11, 0.12)',
                borderColor: targetNetwork.toLowerCase().includes('testnet') ? '#F57C00' : '#F0B90B',
              }
            }}
          >
            Switch to {targetNetwork}
          </Button>
        </Box>
        
        <Typography 
          variant="caption" 
          align="center" 
          sx={{ 
            display: 'block', 
            mt: 2, 
            color: 'text.secondary',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={onClose}
        >
          I'll switch manually
        </Typography>
      </Box>
    </Modal>
  );
};

// Add this array near the top of your file
const CLEAR_KEYS = [
  'wagmi.store',
  'wagmi.connected',
  'wagmi.injected.connected',
  'wagmi.wallet',
  'wagmi.account',
  'wagmi.chainId',
  'rainbowkit.wallet',
  '@appkit/connection_status'
];

const BNBWalletButton = ({ 
  tokenSymbol,
  tokenDecimals,
  tokenAddress,
  tokenType,
  amount,
  paymentAddress,
  networkEnvironment,
  onSuccess,
  onError,
  onTransactionSubmitted,
  onTransactionConfirmed,
  onTransactionFailed,
  expiresAt,
  onWalletConnect,
  paymentDisabled,
  onPaymentInitiated
}) => {
  const [sending, setSending] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [currentChainId, setCurrentChainId] = useState(null);
  const [txHash, setTxHash] = useState(null);
  
  // Network params
  const network = 'bsc';
  const environment = networkEnvironment === 'devnet' || networkEnvironment === 'testnet' 
    ? 'testnet' 
    : 'mainnet';
  const chainId = getChainId(network, environment);
  const tokenInfo = getTokenInfo(network, environment, tokenSymbol);
  const normalizedTokenType = tokenType ? String(tokenType).toLowerCase() : null;
  const isNativeToken = normalizedTokenType === 'native' || (!tokenAddress && tokenSymbol === 'BNB');
  const resolvedTokenAddress = tokenAddress || tokenInfo?.contractAddress;
  const resolvedTokenDecimals = Number.isFinite(Number(tokenDecimals))
    ? Number(tokenDecimals)
    : (tokenInfo?.decimals || 18);
  const explorerBaseUrl = BLOCK_EXPLORERS[network]?.[environment] || null;
  const buildExplorerUrl = (hash) => (explorerBaseUrl && hash ? `${explorerBaseUrl}${hash}` : null);
  const extractTransactionHash = (result) => {
    if (!result) return null;
    if (typeof result === 'string') return result;
    if (typeof result?.hash === 'string') return result.hash;
    if (typeof result?.transactionHash === 'string') return result.transactionHash;
    return null;
  };
  
  // Expiration checks
  const isExpiringSoon = useMemo(() => {
    if (!expiresAt) return false;
    const now = Date.now();
    const expirationTime = toEpochMs(expiresAt);
    if (!Number.isFinite(expirationTime)) return false;
    const timeRemainingMs = expirationTime - now;
    return timeRemainingMs < 60000 && timeRemainingMs > 0;
  }, [expiresAt]);
  
  const isExpired = useMemo(() => {
    if (!expiresAt) return false;
    const now = Date.now();
    const expirationTime = toEpochMs(expiresAt);
    if (!Number.isFinite(expirationTime)) return false;
    return now >= expirationTime;
  }, [expiresAt]);

  // wagmi hooks
  const { address, isConnected, connector } = useAccount();
  const { switchChain, isLoading: isSwitchingNetwork } = useSwitchChain();
  const { disconnect } = useDisconnect();
  
  // Add balance check to ensure adequate funds
  const { data: userBalance } = useBalance({
    address: address,
    token: isNativeToken ? undefined : resolvedTokenAddress,
  });
  
  // Handle native BNB transfers
  const { sendTransactionAsync } = useSendTransaction();
  
  // Handle token transfers (BEP20)
  const { writeContractAsync: sendTokenAsync } = useWriteContract();
  
  // Watch for transaction completion (both native and token transfers)
  useWaitForTransactionReceipt({
    hash: txHash,
    chainId,
    onSuccess(data) {
      const confirmedHash = data.transactionHash || txHash;
      const explorerUrl = buildExplorerUrl(confirmedHash);
      debugLog('Transaction confirmed:', { hash: confirmedHash, explorerUrl });
      if (onTransactionConfirmed) {
        onTransactionConfirmed({
          hash: confirmedHash,
          explorerUrl,
          network,
          environment,
          tokenSymbol,
          amount
        });
      } else if (onSuccess && confirmedHash) {
        onSuccess(`Transaction confirmed! [${confirmedHash.slice(0, 8)}...]`);
      }
      setSending(false);
      setTxHash(null);
    },
    onError(error) {
      console.error("Token transaction failed:", error);
      const normalizedMessage = normalizeWalletError(error);
      if (onTransactionFailed) {
        onTransactionFailed({
          hash: txHash,
          explorerUrl: buildExplorerUrl(txHash),
          network,
          environment,
          tokenSymbol,
          amount,
          errorMessage: normalizedMessage
        });
      } else if (onError) {
        onError(normalizedMessage);
      }
      setSending(false);
      setTxHash(null);
    }
  });
  
  // TESTNET WARNING - similar to SolanaWalletButton.js
  useEffect(() => {
    if (environment === 'testnet' && isConnected) {
      debugLog("TESTNET transaction detected");
      // Only show warning once per session
      const hasShownWarning = sessionStorage.getItem('bsc_testnet_warning_shown');
      
      if (!hasShownWarning) {
        const message = 
          "This is a TESTNET transaction. Please ensure your wallet is:\n\n" +
          "1. Connected to BSC Testnet\n" +
          "2. Has sufficient testnet BNB (you can get from a faucet)\n\n";
        
        alert(message);
        sessionStorage.setItem('bsc_testnet_warning_shown', 'true');
      }
    }
  }, [environment, isConnected]);
  
  // Move the debugLog into a useEffect to prevent it from running on every render
  useEffect(() => {
    debugLog('BSC Payment Environment:', {
      networkEnvironment,
      mappedEnvironment: environment,
      chainId,
      tokenSymbol,
      tokenInfo
    });
  }, [networkEnvironment, environment, chainId, tokenSymbol, tokenInfo]);
  
  // Add logging for the transaction configs
  useEffect(() => {
    if (isConnected) {
      debugLog('Transaction Configs:', {
        isNativeTokenEnabled: isConnected && isNativeToken && !isExpiringSoon && !isExpired,
        sendNativeFunction: !!sendTransactionAsync,
        isTokenEnabled: isConnected && !isNativeToken && resolvedTokenAddress && !isExpiringSoon && !isExpired,
        sendTokenFunction: !!sendTokenAsync
      });
    }
  }, [isConnected, txHash, sendTransactionAsync, sendTokenAsync, isNativeToken, resolvedTokenAddress, isExpiringSoon, isExpired]);
  
  // Add these refs at the top of your component after useState declarations
  const isDisconnecting = useRef(false);
  const prevWalletInfoRef = useRef(null);
  const notificationTimeoutRef = useRef(null);
  
  // The definitive handleDisconnect function
  const handleDisconnect = async (e) => {
    if (e) e.stopPropagation();
    
    debugLog("🔄 ENTERPRISE DISCONNECT: Breaking wallet connections without page refresh");
    
    try {
      // 1. Set flags to block any network modal or operations
      isDisconnecting.current = true;
      setShowNetworkModal(false);
      
      // 2. Immediately notify parent of disconnection
      if (onWalletConnect) {
        onWalletConnect({
          isConnected: false,
          address: null,
          networkName: null,
          tokenSymbol: null,
          isTestnet: null,
          walletType: null,
          balance: null
        });
      }
      
      // 3. Clear all storage keys that could maintain connection
      CLEAR_KEYS.forEach(key => {
        if (localStorage.getItem(key)) {
          debugLog(`Clearing localStorage key: ${key}`);
          localStorage.removeItem(key);
        }
      });
      
      // 4. Add a disconnect marker
      sessionStorage.setItem('wallet_manually_disconnected', 'true');
      
      // 5. Call wagmi disconnect
      if (disconnect) {
        await disconnect();
      }
      
      // 6. Use window.ethereum directly if available
      if (window.ethereum) {
        try {
          if (window.ethereum.disconnect) {
            window.ethereum.disconnect();
          }
        } catch (e) {
          debugLog("Ethereum provider disconnect error (non-fatal):", e);
        }
      }
      
      debugLog("Disconnection complete");
      
      // After disconnection is completed successfully, reset UI state as needed
      setCurrentChainId(null);
      setSending(false);
      isDisconnecting.current = false; // Reset the flag
    } catch (error) {
      console.error("Error during disconnection:", error);
      isDisconnecting.current = false;
    }
  };

  // One-time disconnect state check
  useEffect(() => {
    // Only check once on initial mount
    const wasManuallyDisconnected = sessionStorage.getItem('wallet_manually_disconnected') === 'true';
    
    if (wasManuallyDisconnected) {
      debugLog("🔄 Detected disconnect state - applying once");
      
      // Only force disconnect if actually connected
      if (isConnected && disconnect) {
        debugLog("Still connected, forcing disconnect");
        disconnect();
      }
      
      // Clear the flag to prevent repeated processing
      sessionStorage.removeItem('wallet_manually_disconnected');
    }
  }, [disconnect, isConnected]);
  
  // Main payment handler
  const handlePayment = async () => {
    try {
      // Add an immediate check at the very beginning of the function
      if (isDisconnecting.current) {
        debugLog("⛔ BLOCKED: Payment attempt during active disconnection");
        return;
      }
      
      // Block payment if wallet shows as disconnected
      if (!isConnected || !address) {
        debugLog("⛔ BLOCKED: Payment attempt with disconnected wallet");
        return;
      }

      debugLog("Button clicked, starting payment process");
      setSending(true);
      
      // Get current chain with additional safeguards
      let currentChainId;
      
      try {
        // Double-check disconnect status before proceeding with provider operations
        if (isDisconnecting.current) {
          debugLog("⛔ BLOCKED: Chain check during disconnection");
          setSending(false);
          return;
        }
        
        const provider = await connector?.getProvider();
        
        if (provider) {
          // Another disconnect check before chain ID request
          if (isDisconnecting.current) {
            debugLog("⛔ BLOCKED: Chain ID request during disconnection");
            setSending(false);
            return;
          }
          
          const chainIdHex = await provider.request({ method: 'eth_chainId' });
          currentChainId = parseInt(chainIdHex, 16);
          setCurrentChainId(currentChainId);
        }
      } catch (e) {
        console.error("Error getting chain ID:", e);
        // Don't break the payment flow on chain ID error
      }
      
      debugLog("Current chain:", currentChainId, "Target chain:", chainId);
      
      // More aggressive check before showing network modal
      if (currentChainId && 
          currentChainId !== chainId && 
          !isDisconnecting.current) {
        
        // Final safety check - don't show modal if we're disconnecting
        if (isDisconnecting.current) {
          debugLog("⛔ BLOCKED: Network modal during disconnection");
          setSending(false);
          return;
        }
        
        debugLog("Network mismatch confirmed:", {currentChainId, targetChainId: chainId});
        setShowNetworkModal(true);
        setSending(false);
        return;
      }

      if (onPaymentInitiated) {
        onPaymentInitiated();
      }
      
      // Parse amounts here
      let parsedAmount;
      let parsedTokenAmount;

      try {
        // For native BNB, parse amount to wei (18 decimals)
        if (isNativeToken) {
          debugLog("Parsing BNB amount:", amount);
          parsedAmount = parseUnits(
            formatTokenAmount(amount, resolvedTokenDecimals), 
            resolvedTokenDecimals
          );
          debugLog("Parsed amount in wei:", parsedAmount.toString());
        } 
        // For tokens, use token's decimals
        else if (resolvedTokenAddress) {
          debugLog("Parsing token amount for:", tokenSymbol);
          parsedTokenAmount = parseUnits(
            formatTokenAmount(amount, resolvedTokenDecimals), 
            resolvedTokenDecimals
          );
          debugLog("Parsed token amount:", parsedTokenAmount.toString());
        }
      } catch (parseError) {
        console.error("Error parsing amount:", parseError);
        onError(`Amount parsing error: ${parseError.message}`);
        setSending(false);
        return;
      }

      // PAYMENT PROCESSING CODE
      if (isNativeToken) {
        // Send native BNB
        const sendNativeExists = !!sendTransactionAsync;
        debugLog("Attempting to send native BNB", { 
          sendNativeExists, 
          amount, 
          paymentAddress,
          parsedAmount: parsedAmount?.toString() 
        });

        if (sendTransactionAsync && parsedAmount) {
          try {
            debugLog("Submitting transaction with parameters:", {
              to: paymentAddress,
              value: parsedAmount.toString(),
              chainId: chainId
            });

            const txResult = await sendTransactionAsync({
              to: paymentAddress,
              value: parsedAmount,
              chainId: chainId
            });

            const submittedHash = extractTransactionHash(txResult);
            debugLog("Transaction submitted, hash:", submittedHash);
            if (submittedHash) {
              setTxHash(submittedHash);
              if (onTransactionSubmitted) {
                onTransactionSubmitted({
                  hash: submittedHash,
                  explorerUrl: buildExplorerUrl(submittedHash),
                  network,
                  environment,
                  tokenSymbol,
                  amount
                });
              }
            } else if (onError) {
              onError("Transaction submitted without a hash");
            }
          } catch (error) {
            console.error("Error sending transaction:", error);
            onError(normalizeWalletError(error));
            setSending(false);
          }
        } else {
          onError("Cannot send transaction: Wallet connection issue");
          setSending(false);
        }
      } else if (resolvedTokenAddress && sendTokenAsync) {
        // Send token (BEP-20) - SAME PATTERN AS BNB
        try {
          debugLog("Attempting to send token transaction");
          if (parsedTokenAmount) {
            debugLog("Submitting token transaction with parameters:", {
              contractAddress: resolvedTokenAddress,
              args: [paymentAddress, parsedTokenAmount],
            });

            // Direct call like BNB uses sendTransactionAsync
            const txResult = await sendTokenAsync({
              address: resolvedTokenAddress,
              abi: tokenAbi,
              functionName: 'transfer',
              args: [paymentAddress, parsedTokenAmount],
            });
            
            const submittedHash = extractTransactionHash(txResult);
            debugLog("Token transaction submitted, hash:", submittedHash);
            if (submittedHash) {
              setTxHash(submittedHash);
              if (onTransactionSubmitted) {
                onTransactionSubmitted({
                  hash: submittedHash,
                  explorerUrl: buildExplorerUrl(submittedHash),
                  network,
                  environment,
                  tokenSymbol,
                  amount
                });
              }
            } else if (onError) {
              onError("Transaction submitted without a hash");
            }
          } else {
            throw new Error("Invalid token amount");
          }
        } catch (error) {
          console.error("Error sending token transaction:", error);
          onError(normalizeWalletError(error));
          setSending(false);
        }
      } else {
        console.error("🚨 TRANSACTION DEBUG:", {
          tokenSymbol,
          isNativeToken,
          tokenInfo,
          hasContractAddress: !!resolvedTokenAddress,
          sendTokenExists: !!sendTokenAsync,
          tokenConfig: txHash,
          isSimulationEnabled: isConnected && !isNativeToken && resolvedTokenAddress && !isExpiringSoon && !isExpired
        });
        console.error("No valid transaction method available");
        onError("Transaction failed: No valid payment method");
        setSending(false);
      }
    } catch (error) {
      console.error("Error in payment process:", error);
      onError(`Payment failed: ${error.message}`);
      setSending(false);
    }
  };
  
  // Show correct button state
  const isButtonDisabled = 
    sending || 
    isExpiringSoon || 
    isExpired || 
    isSwitchingNetwork ||
    paymentDisabled;
  
  useEffect(() => {
    // Listen for WalletConnect errors
    const handleWalletConnectError = (event) => {
      const errorString = event?.reason?.toString() || '';
      
      // Silently catch and suppress the "Proposal expired" error
      if (errorString.includes('Proposal expired')) {
        debugLog('Silently handling WalletConnect proposal expired error');
        
        // Prevent the error from bubbling up to the UI
        event.preventDefault?.();
        event.stopPropagation?.();
        
        // Just reset the loading state without showing an error
        setSending(false);
        
        // Optional: silently retry the connection after a short delay
        // setTimeout(() => {
        //   // Attempt to reconnect
        // }, 500);
        
        return false;
      }
    };
    
    window.addEventListener('error', handleWalletConnectError, true);
    window.addEventListener('unhandledrejection', handleWalletConnectError, true);
    
    return () => {
      window.removeEventListener('error', handleWalletConnectError, true);
      window.removeEventListener('unhandledrejection', handleWalletConnectError, true);
    };
  }, []);
  
  useEffect(() => {
    // Handle MetaMask "already pending" errors
    const clearPendingMetaMaskRequests = () => {
      // Check if window.ethereum exists (MetaMask or similar injected provider)
      if (window.ethereum) {
        try {
          // Force disconnect if we have any pending requests
          if (typeof window.ethereum._state?.accounts !== 'undefined') {
            debugLog('Attempting to clear any pending MetaMask requests');
            
            // Try to reset the connection state
            window.ethereum._state = {
              ...window.ethereum._state,
              isConnected: false,
              isPermanentlyDisconnected: false,
              initialized: true
            };
            
            // Force clear any pending requests in localStorage
            Object.keys(localStorage).forEach(key => {
              if (key.includes('metamask') || key.includes('WALLETCONNECT')) {
                localStorage.removeItem(key);
              }
            });
            
            // If we're using RainbowKit, also try to clear its state
            if (disconnect && !isConnected) {
              disconnect();
            }
          }
        } catch (error) {
          console.error('Error clearing MetaMask state:', error);
        }
      }
    };
    
    // Run on component mount
    clearPendingMetaMaskRequests();
    
    // Add a click handler to the document to help clear pending requests
    const handleClick = () => {
      if (!isConnected) {
        clearPendingMetaMaskRequests();
      }
    };
    
    document.addEventListener('click', handleClick);
    
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [disconnect, isConnected]);
  
  useEffect(() => {
    // Check URL params for reset flag
    const params = new URLSearchParams(window.location.search);
    const shouldReset = params.get('reset_wallet') === 'true';
    
    // Reset wallet connection on page refresh if the flag is present
    if (shouldReset && disconnect && isConnected) {
      debugLog("Resetting wallet connection based on URL parameter");
      disconnect();
      
      // Also clear localStorage wallet data
      Object.keys(localStorage).forEach(key => {
        if (key.includes('wallet') || key.includes('WALLET') || 
            key.includes('metamask') || key.includes('connect')) {
          localStorage.removeItem(key);
        }
      });
    }
  }, [disconnect, isConnected]);
  
  // Replace your wallet notification useEffect with this one
  useEffect(() => {
    // Only notify parent if wallet is connected and information has changed
    if (onWalletConnect) {
      const currentWalletInfo = isConnected && address ? {
        isConnected,
        address,
        networkName: environment === 'mainnet' ? 'BNB Chain' : 'BNB Chain Testnet',
        tokenSymbol,
        isTestnet: environment !== 'mainnet',
        walletType: connector?.name || 'Wallet',
        balance: userBalance?.formatted
      } : {
        isConnected: false,
        address: null,
        networkName: null,
        tokenSymbol: null,
        isTestnet: null,
        walletType: null,
        balance: null
      };

      // Deep compare previous and current wallet info to avoid unnecessary updates
      const hasChanged = JSON.stringify(prevWalletInfoRef.current) !== JSON.stringify(currentWalletInfo);
      
      if (hasChanged) {
        // Clear any existing timeout to prevent multiple rapid updates
        if (notificationTimeoutRef.current) {
          clearTimeout(notificationTimeoutRef.current);
        }
        
        // Use a small timeout to debounce multiple rapid state changes
        notificationTimeoutRef.current = setTimeout(() => {
          debugLog("Sending wallet info to parent", currentWalletInfo);
          onWalletConnect(currentWalletInfo);
          prevWalletInfoRef.current = currentWalletInfo;
        }, 300);
      }
    }
    
    // Cleanup function to clear timeout if component unmounts
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, [
    isConnected, 
    address, 
    environment, 
    connector?.name, 
    userBalance?.formatted, 
    tokenSymbol,
    onWalletConnect
  ]);

  return (
    <>
      {/* Network Switch Modal */}
      <NetworkSwitchModal 
        open={showNetworkModal}
        onClose={() => setShowNetworkModal(false)}
        targetNetwork={environment === 'mainnet' ? 'BNB Chain' : 'BNB Chain Testnet'}
        targetChainId={chainId}
        currentChainId={currentChainId}
        switchChain={switchChain}
      />
      
      {isConnected ? (
        <WalletButton
          variant="contained"
          onClick={handlePayment}
          disabled={isButtonDisabled}
          sx={{
            bgcolor: '#384df5',
            '&:hover': { bgcolor: '#2b3eda' },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 16px'
          }}
        >
          {sending ? (
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'center' }}>
              <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
              <span>Processing...</span>
            </Box>
          ) : (
            <>
              <Box
                sx={{ flexGrow: 1, textAlign: 'center' }}
              >
                <Tooltip 
                  title={`Confirm transaction on ${environment === 'mainnet' ? 'BNB Chain' : 'BNB Chain Testnet'}`}
                  placement="top"
                  PopperProps={{
                    modifiers: [{
                      name: 'preventOverflow',
                      options: {
                        altBoundary: true,
                      },
                    }],
                  }}
                >
                  <span>{`Pay with ${tokenSymbol}`}</span>
                </Tooltip>
              </Box>
              <ButtonDivider orientation="vertical" flexItem />
              {/* Wrap in a Box to isolate from parent tooltip */}
              <Box
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={(e) => e.stopPropagation()}
                onMouseOver={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                onMouseLeave={(e) => e.stopPropagation()}
                onPointerOver={(e) => e.stopPropagation()}
                onPointerEnter={(e) => e.stopPropagation()}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Tooltip 
                  title="Disconnect wallet" 
                  placement="top"
                  PopperProps={{
                    modifiers: [{
                      name: 'preventOverflow',
                      options: {
                        altBoundary: true,
                      },
                    }],
                  }}
                >
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDisconnect(e);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDisconnect(e);
                      }
                    }}
                    sx={{
                      color: '#33FF33', // Bright Yoda green
                      padding: '4px',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: '#F44336',
                      }
                    }}
                    aria-label="disconnect wallet"
                  >
                    <PowerSettingsNewIcon fontSize="small" />
                  </Box>
                </Tooltip>
              </Box>
            </>
          )}
        </WalletButton>
      ) : (
        <ConnectButton.Custom>
          {({
            account,
            chain,
            openAccountModal,
            openChainModal,
            openConnectModal,
            mounted,
          }) => {
            return (
              <WalletButton
                onClick={openConnectModal}
                sx={{
                  bgcolor: '#384df5',
                  '&:hover': { bgcolor: '#2b3eda' },
                }}
                startIcon={<AccountBalanceWalletIcon />}
              >
                Connect Wallet
              </WalletButton>
            );
          }}
        </ConnectButton.Custom>
      )}
    </>
  );
};

export default BNBWalletButton;
